// Form Validator Component for Fortisys Website
// Based on Technical Specification Section 6.2

export class FormValidator {
  constructor(formElement, options = {}) {
    this.form = formElement;
    this.options = {
      validateOnInput: true,
      validateOnBlur: true,
      showSuccessMessages: false,
      ...options
    };

    this.rules = new Map();
    this.errors = new Map();
    this.isValid = false;

    this.init();
  }

  init() {
    this.setupDefaultRules();
    this.bindEvents();
    this.setupAccessibility();
  }

  setupDefaultRules() {
    // Set up common validation rules
    const emailInputs = this.form.querySelectorAll('input[type="email"]');
    emailInputs.forEach(input => {
      this.addRule(input.name || input.id, this.isValidEmail, 'Please enter a valid email address');
    });

    const requiredInputs = this.form.querySelectorAll('[required]');
    requiredInputs.forEach(input => {
      this.addRule(input.name || input.id, this.isRequired, 'This field is required');
    });

    const phoneInputs = this.form.querySelectorAll('input[type="tel"]');
    phoneInputs.forEach(input => {
      this.addRule(input.name || input.id, this.isValidPhone, 'Please enter a valid phone number');
    });
  }

  bindEvents() {
    // Form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Input validation
    if (this.options.validateOnInput) {
      this.form.addEventListener('input', (e) => {
        if (e.target.matches('input, textarea, select')) {
          this.validateField(e.target);
        }
      });
    }

    // Blur validation
    if (this.options.validateOnBlur) {
      this.form.addEventListener('blur', (e) => {
        if (e.target.matches('input, textarea, select')) {
          this.validateField(e.target);
        }
      }, true);
    }
  }

  setupAccessibility() {
    // Ensure form has proper ARIA attributes
    if (!this.form.getAttribute('novalidate')) {
      this.form.setAttribute('novalidate', '');
    }

    // Set up error containers
    const fields = this.form.querySelectorAll('input, textarea, select');
    fields.forEach(field => {
      const fieldName = field.name || field.id;
      const errorContainer = this.form.querySelector(`#${fieldName}-error`);

      if (errorContainer) {
        field.setAttribute('aria-describedby', errorContainer.id);
        errorContainer.setAttribute('role', 'alert');
        errorContainer.setAttribute('aria-live', 'polite');
      }
    });
  }

  addRule(fieldName, validator, message) {
    if (!this.rules.has(fieldName)) {
      this.rules.set(fieldName, []);
    }
    this.rules.get(fieldName).push({ validator, message });
  }

  validateField(field) {
    const fieldName = field.name || field.id;
    const fieldRules = this.rules.get(fieldName);

    if (!fieldRules) return true;

    const value = field.value.trim();
    let isFieldValid = true;
    let errorMessage = '';

    for (const rule of fieldRules) {
      if (!rule.validator(value, field)) {
        isFieldValid = false;
        errorMessage = rule.message;
        break;
      }
    }

    if (isFieldValid) {
      this.clearFieldError(field);
      if (this.options.showSuccessMessages) {
        this.showFieldSuccess(field);
      }
    } else {
      this.showFieldError(field, errorMessage);
    }

    return isFieldValid;
  }

  validateForm() {
    let isFormValid = true;

    // Validate all fields with rules
    for (const [fieldName] of this.rules) {
      const field = this.form.querySelector(`[name="${fieldName}"], #${fieldName}`);
      if (field && !this.validateField(field)) {
        isFormValid = false;
      }
    }

    this.isValid = isFormValid;
    return isFormValid;
  }

  showFieldError(field, message) {
    const fieldName = field.name || field.id;
    const errorContainer = this.form.querySelector(`#${fieldName}-error`);

    // Add error class to field
    field.classList.add('form-input--error');
    field.setAttribute('aria-invalid', 'true');

    // Show error message
    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.style.display = 'block';
    }

    // Add error class to form group
    const formGroup = field.closest('.form-group');
    if (formGroup) {
      formGroup.classList.add('form-group--error');
    }

    this.errors.set(fieldName, message);
  }

  clearFieldError(field) {
    const fieldName = field.name || field.id;
    const errorContainer = this.form.querySelector(`#${fieldName}-error`);

    // Remove error class from field
    field.classList.remove('form-input--error');
    field.setAttribute('aria-invalid', 'false');

    // Hide error message
    if (errorContainer) {
      errorContainer.textContent = '';
      errorContainer.style.display = 'none';
    }

    // Remove error class from form group
    const formGroup = field.closest('.form-group');
    if (formGroup) {
      formGroup.classList.remove('form-group--error');
    }

    this.errors.delete(fieldName);
  }

  showFieldSuccess(field) {
    const fieldName = field.name || field.id;
    const successContainer = this.form.querySelector(`#${fieldName}-success`);

    // Add success class to field
    field.classList.add('form-input--success');

    // Show success message
    if (successContainer) {
      successContainer.textContent = 'Valid';
      successContainer.style.display = 'block';
    }

    // Add success class to form group
    const formGroup = field.closest('.form-group');
    if (formGroup) {
      formGroup.classList.add('form-group--success');
    }
  }

  handleSubmit() {
    if (this.validateForm()) {
      this.onValidSubmit();
    } else {
      this.onInvalidSubmit();
    }
  }

  onValidSubmit() {
    // Dispatch custom event
    const event = new CustomEvent('formValidSubmit', {
      detail: {
        formData: new FormData(this.form),
        validator: this
      }
    });

    this.form.dispatchEvent(event);

    // Default behavior: submit the form
    if (!event.defaultPrevented) {
      this.submitForm();
    }
  }

  onInvalidSubmit() {
    // Focus first invalid field
    const firstInvalidField = this.form.querySelector('.form-input--error');
    if (firstInvalidField) {
      firstInvalidField.focus();
    }

    // Dispatch custom event
    const event = new CustomEvent('formInvalidSubmit', {
      detail: {
        errors: this.errors,
        validator: this
      }
    });

    this.form.dispatchEvent(event);
  }

  async submitForm() {
    const submitButton = this.form.querySelector('button[type="submit"]');
    const formData = new FormData(this.form);

    // Show loading state
    if (submitButton) {
      this.setButtonLoading(submitButton, true);
    }

    try {
      // Determine API endpoint based on form ID
      let apiEndpoint = '/api/submit-demo';
      if (this.form.id === 'demo-request-form') {
        apiEndpoint = '/api/submit-demo';
      }

      // Convert FormData to JSON object
      const formDataObj = {};
      for (const [key, value] of formData.entries()) {
        formDataObj[key] = value;
      }

      // Submit to our serverless function
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formDataObj)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this.onSubmitSuccess(response, result);
      } else {
        // Handle API error responses
        const errorMessage = result.message || result.error || `Server error: ${response.status}`;
        this.onSubmitError(new Error(errorMessage), result);
      }
    } catch (error) {
      this.onSubmitError(error);
    } finally {
      // Remove loading state
      if (submitButton) {
        this.setButtonLoading(submitButton, false);
      }
    }
  }

  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.classList.add('btn--loading');
      button.disabled = true;

      // Store original text and show loading text
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }

      const loadingText = button.dataset.loadingText || 'Submitting...';
      button.textContent = loadingText;
    } else {
      button.classList.remove('btn--loading');
      button.disabled = false;

      // Restore original text
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
      }
    }
  }

  onSubmitSuccess(response, result = {}) {
    // Show success message from API or default
    const successMessage = result.message || 'Form submitted successfully!';
    this.showFormMessage(successMessage, 'success');

    // Show additional success details if available
    if (result.data) {
      const detailsMessage = `Thank you, ${result.data.contact}! We have received your demo request for ${result.data.company}.`;
      setTimeout(() => {
        this.showFormMessage(detailsMessage, 'info');
      }, 2000);
    }

    // Reset form after successful submission
    setTimeout(() => {
      this.reset();
      this.hideFormMessage();
    }, 5000);

    // Hide form and show success state
    this.showSuccessState(result);

    // Dispatch success event
    const event = new CustomEvent('formSubmitSuccess', {
      detail: { response, result, validator: this }
    });

    this.form.dispatchEvent(event);
  }

  onSubmitError(error, result = {}) {
    // Show specific error message from API or generic message
    let errorMessage = error.message || 'An error occurred while submitting the form. Please try again.';

    // Handle validation errors from server
    if (result.missingFields && result.missingFields.length > 0) {
      errorMessage = `Please fill in the following required fields: ${result.missingFields.join(', ')}`;
    }

    this.showFormMessage(errorMessage, 'error');

    // Focus on first error field if available
    if (result.missingFields && result.missingFields.length > 0) {
      const firstErrorField = this.form.querySelector(`[name="${result.missingFields[0]}"]`);
      if (firstErrorField) {
        firstErrorField.focus();
      }
    }

    // Dispatch error event
    const event = new CustomEvent('formSubmitError', {
      detail: { error, result, validator: this }
    });

    this.form.dispatchEvent(event);
  }

  showSuccessState(result) {
    // Create success overlay or replace form content
    const formContainer = this.form.closest('.demo-form-section__form');
    if (!formContainer) return;

    const successHTML = `
      <div class="form-success" role="alert" aria-live="polite">
        <div class="form-success__icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="32" fill="#10b981"/>
            <path d="M21 32L28 39L43 25" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="form-success__title">Demo Request Submitted!</h3>
        <p class="form-success__message">
          Thank you for your interest in Fortisys. We have received your demo request and will contact you within 24 hours to schedule your personalized demonstration.
        </p>
        ${result.data ? `
          <div class="form-success__details">
            <p><strong>Contact:</strong> ${result.data.contact}</p>
            <p><strong>Company:</strong> ${result.data.company}</p>
            <p><strong>Submitted:</strong> ${new Date(result.data.submittedAt).toLocaleString()}</p>
          </div>
        ` : ''}
        <div class="form-success__actions">
          <a href="/" class="btn btn--primary">Return to Homepage</a>
          <button type="button" class="btn btn--secondary" onclick="location.reload()">Submit Another Request</button>
        </div>
      </div>
    `;

    // Replace form content with success state
    formContainer.innerHTML = successHTML;
  }

  showFormMessage(message, type = 'info') {
    // Remove existing message
    this.hideFormMessage();

    // Create message element
    const messageElement = document.createElement('div');
    messageElement.className = `form-message form-message--${type}`;
    messageElement.textContent = message;
    messageElement.setAttribute('role', 'alert');
    messageElement.setAttribute('aria-live', 'polite');

    // Insert at top of form
    this.form.insertBefore(messageElement, this.form.firstChild);

    // Add animation
    setTimeout(() => {
      messageElement.classList.add('form-message--visible');
    }, 100);
  }

  hideFormMessage() {
    const existingMessage = this.form.querySelector('.form-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }

  // Validation rules
  isRequired(value) {
    return value.length > 0;
  }

  isValidEmail(value) {
    if (!value) return true; // Let required rule handle empty values
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  isValidPhone(value) {
    if (!value) return true; // Let required rule handle empty values
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(value.replace(/\s/g, ''));
  }

  isMinLength(minLength) {
    return (value) => value.length >= minLength;
  }

  isMaxLength(maxLength) {
    return (value) => value.length <= maxLength;
  }

  // Public API methods
  reset() {
    this.form.reset();
    this.errors.clear();

    // Clear all error states
    const errorFields = this.form.querySelectorAll('.form-input--error, .form-input--success');
    errorFields.forEach(field => {
      this.clearFieldError(field);
    });
  }

  getFormData() {
    return new FormData(this.form);
  }

  getErrors() {
    return new Map(this.errors);
  }

  destroy() {
    // Remove event listeners
    this.form.removeEventListener('submit', this.handleSubmit);

    // Clear validation states
    this.reset();
  }
}
